/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  ButtonExportCSVAsync,
  Card,
  CardContent,
  CardIconStatus,
  CardLabel,
  CardRow,
  CardValue,
  Empty,
  EmptyTable,
  FieldWrapper,
  Filter,
  FilterButton,
  HeaderCell,
  HeaderRow,
  Input,
  PaginatedList,
  Section,
  SectionHeader,
  Table,
  TableBody,
  TableHead,
} from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { useDebounce, useQueryString } from 'hooks';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { getSiteTipAcceptancePaymentSetsRequest, Payments, selectSiteTipAcceptancePaymentSets } from 'store/Collections';
import { getPaymentsRequest, selectTipAcceptancePayments, TipAcceptance } from 'store/TipAcceptance';
import { IStore } from 'store/types';
import { ApiQueryOperator, IAcceptancePayment, IApiQueryParam } from 'types';
import { CSVFormatter } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import * as API from '../../api';
import { TipAcceptancePaymentRow } from './components';

const CardRowWrapper = styled(CardRow)`
  margin-top: ${toRem(-theme.card.minHeight.large / 4)};

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(22)};
    margin-top: ${toRem(-theme.card.minHeight.large / 3.2)};
  }
`;

const FilterContainer = styled.div`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  z-index: ${theme.zIndex.contentHeader + 50};

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;

    > div {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }
  }
`;

interface IParams {
  payment_set_id: string;
}

const TipAcceptanceEmployeePayments = () => {
  const { payment_set_id } = useParams<IParams>();
  const dispatch = useDispatch();
  const [urlQuery, setUrlQuery] = useQueryString('tipAcceptancePayouts', window.location);

  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId || '';

  const paymentSets = useSelector(selectSiteTipAcceptancePaymentSets());
  const payouts = useSelector(selectTipAcceptancePayments());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, TipAcceptance.GET_PAYMENTS_REQUEST));
  const isPaymentSetLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_TIP_ACCEPTANCE_PAYMENT_SETS_REQUEST));

  const employeeTextSearch = (urlQuery.q || []).find((q: IApiQueryParam) => q.key === 'employee_name' && q.operator === ApiQueryOperator.like)?.value;
  const cardholderTextSearch = (urlQuery.q || []).find((q: IApiQueryParam) => q.key === 'payor.name' && q.operator === ApiQueryOperator.like)?.value;

  const [filterVisible, setFilterVisible] = useState<boolean>(!isEmpty(employeeTextSearch) || !isEmpty(cardholderTextSearch));

  const onQueryChange = (options: IApiQueryOptions) => {
    setUrlQuery(options);
  };

  const onSearchChange = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value?.trim();
    const newQuery = urlQuery.q?.filter((query: IApiQueryParam) => query.key !== key) || [];

    onQueryChange({ q: value ? [...newQuery, { key, operator: ApiQueryOperator.like, value }] : newQuery, offset: undefined });
  };

  const debouncedSearch = useDebounce(onSearchChange);
  const paymentSetQuery = { key: 'payment_set_id', operator: ApiQueryOperator.eq, value: payment_set_id };

  useEffect(() => {
    if (currentSiteId) {
      // get payment set that contains payouts
      dispatch(getSiteTipAcceptancePaymentSetsRequest(currentSiteId, { q: [{ ...paymentSetQuery, key: 'id' }] }));
    }
  }, [dispatch, currentSiteId, payment_set_id]);

  useEffect(() => {
    if (currentSiteId) {
      dispatch(
        getPaymentsRequest(currentSiteId, {
          sort: '-created_at',
          limit: 10,
          ...urlQuery,
          q: [...(urlQuery?.q || []), paymentSetQuery],
        })
      );
    }
  }, [dispatch, currentSiteId, payment_set_id, urlQuery]);

  const exportCSV = async (siteId: string) => {
    const response = await API.Sites.getTipAcceptancePayments(siteId, {
      ...urlQuery,
      q: [...(urlQuery?.q || []), paymentSetQuery],
      limit: 0,
      offset: 0,
    });
    return CSVFormatter.acceptanceEmployeePayments(response.body);
  };

  const acceptancePaymentSet = paymentSets?.data.find((item) => item.id === payment_set_id);

  return (
    <>
      <CardRowWrapper>
        <Card data-cy="employees-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="People" isLoading={isPaymentSetLoading} />
          <CardContent>
            <CardLabel isLoading={isPaymentSetLoading}>Employees</CardLabel>
            <CardValue isLoading={isPaymentSetLoading}>{acceptancePaymentSet?.totals.employees}</CardValue>
          </CardContent>
        </Card>
        <Card data-cy="tips-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="Receipt" isLoading={isPaymentSetLoading} />
          <CardContent>
            <CardLabel isLoading={isPaymentSetLoading}>Tips</CardLabel>
            <CardValue isLoading={isPaymentSetLoading}>{acceptancePaymentSet?.totals.payments}</CardValue>
          </CardContent>
        </Card>
        <Card data-cy="amount-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="Receipt" isLoading={isPaymentSetLoading} />
          <CardContent>
            <CardLabel isLoading={isPaymentSetLoading}>Tip Total</CardLabel>
            <CardValue isLoading={isPaymentSetLoading}>{centsToDollarsDisplay(acceptancePaymentSet?.totals.amount || 0)} </CardValue>
          </CardContent>
        </Card>
        <Card data-cy="fees-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="Receipt" isLoading={isPaymentSetLoading} />
          <CardContent>
            <CardLabel isLoading={isPaymentSetLoading}>Fees</CardLabel>
            <CardValue isLoading={isPaymentSetLoading}>{centsToDollarsDisplay(acceptancePaymentSet?.totals.fees || 0)} </CardValue>
          </CardContent>
        </Card>
        <Card data-cy="net-card">
          <CardIconStatus color={Appearance.NEUTRAL} icon="Receipt" isLoading={isPaymentSetLoading} />
          <CardContent>
            <CardLabel isLoading={isPaymentSetLoading}>Net</CardLabel>
            <CardValue isLoading={isPaymentSetLoading}>{centsToDollarsDisplay(acceptancePaymentSet?.totals.net || 0)} </CardValue>
          </CardContent>
        </Card>
      </CardRowWrapper>

      <Section data-cy="table">
        <SectionHeader isLoading={isLoading} title="Tips" value={`(${payouts?.total || 0})`} zIndex={8}>
          <FilterButton filterActive={filterVisible} appliedFilters={urlQuery.q?.length} onClick={() => setFilterVisible(!filterVisible)} />
          <ButtonExportCSVAsync disabled={!payouts || payouts.total === 0} exportFunc={() => exportCSV(currentSiteId)} fileName={`${payment_set_id}_tips`} />
        </SectionHeader>

        <FilterContainer>
          <Filter isVisible={filterVisible}>
            <FieldWrapper label="Employee" data-cy="employee-filter">
              <Input placeholder="Search name" defaultValue={employeeTextSearch} onChange={(e) => debouncedSearch('employee_name', e)} />
            </FieldWrapper>

            <FieldWrapper label="Cardholder" data-cy="cardholder-filter">
              <Input placeholder="Search name" defaultValue={cardholderTextSearch} onChange={(e) => debouncedSearch('payor.name', e)} />
            </FieldWrapper>
          </Filter>
        </FilterContainer>

        <PaginatedList total={payouts?.total} limit={payouts?.limit} offset={payouts?.offset} onPageChange={onQueryChange} isLoading={isLoading || !payouts?.total} standalone={false}>
          <Table scrollable sticky>
            <TableHead>
              <HeaderRow sticky>
                <HeaderCell>Employee</HeaderCell>
                <HeaderCell>Tip</HeaderCell>
                <HeaderCell>Fees</HeaderCell>
                <HeaderCell>Net</HeaderCell>
                <HeaderCell>Cardholder</HeaderCell>
                <HeaderCell>Payment Method</HeaderCell>
              </HeaderRow>
            </TableHead>

            <TableBody>
              {payouts?.data.map((payment: IAcceptancePayment) => (
                <TipAcceptancePaymentRow key={`acceptance-payment-${payment.id}`} payment={payment} isLoading={isLoading} />
              ))}

              {!isLoading && payouts?.total === 0 && (
                <EmptyTable>
                  <Empty icon="Payment" message="No payments found." />
                </EmptyTable>
              )}
            </TableBody>
          </Table>
        </PaginatedList>
      </Section>
    </>
  );
};

export default TipAcceptanceEmployeePayments;
